import React, { useState, useEffect, useRef } from 'react';
import { tournamentApi } from '../../../services/api';
import toast from 'react-hot-toast';

// ─── Scoring validators ────────────────────────────────────────────────────────

/** Badminton: rally scoring.
 *  15-pt format: deuce at 14-14, golden point at 21-20 (hard cap 21)
 *  21-pt format: deuce at 20-20, golden point at 30-29 (hard cap 30)
 */
const validateBadmintonGame = (p1, p2, pointsPerSet) => {
  const target = parseInt(pointsPerSet) || 21;
  const deuceTrigger = target - 1;           // 14 for 15-pt, 20 for 21-pt
  const hardCap = target === 15 ? 21 : 30;   // golden-point ceiling

  const a = parseInt(p1) || 0;
  const b = parseInt(p2) || 0;
  if (a === 0 && b === 0) return '';
  if (a < 0 || b < 0) return 'Scores cannot be negative';
  if (a > hardCap || b > hardCap) return `Score cannot exceed ${hardCap} (${target}-pt format, golden point at ${hardCap}-${hardCap - 1})`;

  const winner = Math.max(a, b), loser = Math.min(a, b), diff = winner - loser;

  // Golden-point ceiling reached
  if (winner === hardCap) return diff <= 2 ? '' : 'Invalid: game should have ended earlier';

  // Extended play beyond target (deuce territory)
  if (winner > target) {
    if (diff === 2) return '';
    if (diff === 1) return `Deuce: need 2-point lead, or golden point at ${hardCap}-${hardCap - 1}`;
    return 'Invalid: game should have ended earlier';
  }

  // Exactly at target
  if (winner === target) {
    if (loser >= deuceTrigger) {
      // Both reached deuce threshold — need 2-pt lead
      return diff >= 2 ? '' : `Deuce at ${deuceTrigger}-${deuceTrigger}: need 2-point lead`;
    }
    return ''; // normal win
  }

  return 'Game not complete';
};

/** Tennis/Padel: game-based set validation. Valid: 6-0..6-4, 7-5, 7-6 */
const validateTennisSet = (a, b) => {
  if (a === 0 && b === 0) return '';
  if (a < 0 || b < 0) return 'Games cannot be negative';
  if (a > 7 || b > 7) return 'Max 7 games per set';
  if ((a === 6 && b <= 4) || (b === 6 && a <= 4)) return '';
  if ((a === 7 && b === 5) || (b === 7 && a === 5)) return '';
  if ((a === 7 && b === 6) || (b === 7 && a === 6)) return '';
  if (Math.max(a, b) < 6) return 'Set not complete';
  return 'Invalid tennis set score';
};

/** Table Tennis / Squash: 11-point rally, win by 2, no hard cap */
const validateTTGame = (a, b, pts = 11) => {
  if (a === 0 && b === 0) return '';
  if (a < 0 || b < 0) return 'Scores cannot be negative';
  if (a > 30 || b > 30) return 'Score exceeds practical limit';
  if (Math.max(a, b) > pts && Math.abs(a - b) > 2) return 'Game should have ended earlier';
  if (a >= pts && (a - b) >= 2) return '';
  if (b >= pts && (b - a) >= 2) return '';
  if (a >= pts - 1 && b >= pts - 1) return 'Deuce: need 2 point lead';
  return 'Game not complete';
};

/** Pickleball: 11-point rally, win by 2, hard cap at 21 */
const validatePickleballGame = (a, b, pts = 11) => {
  if (a === 0 && b === 0) return '';
  if (a < 0 || b < 0) return 'Scores cannot be negative';
  if (a > 21 || b > 21) return 'Score exceeds 21-point cap';
  if (a === 21 || b === 21) return '';
  if (Math.max(a, b) > pts && Math.abs(a - b) > 2) return 'Game should have ended earlier';
  if (a >= pts && (a - b) >= 2) return '';
  if (b >= pts && (b - a) >= 2) return '';
  if (a >= pts - 1 && b >= pts - 1) return 'Deuce: need 2 point lead';
  return 'Game not complete';
};

// ─── Main component ────────────────────────────────────────────────────────────

const formatElapsed = (startTime) => {
  if (!startTime) return null;
  const start = new Date(startTime);
  const diff = Math.floor((Date.now() - start.getTime()) / 1000);
  if (diff < 0) return null;
  const m = Math.floor(diff / 60);
  const s = diff % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const formatDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return null;
  const diff = Math.round((new Date(endTime) - new Date(startTime)) / 60000);
  return diff > 0 ? `${diff} min` : null;
};

const ScoreModal = ({ match, sport, getRoundName, onClose, onSaved }) => {
  const [startingMatch, setStartingMatch] = useState(false);
  const [elapsed, setElapsed] = useState(() => formatElapsed(match.match_start_time));
  const timerRef = useRef(null);

  useEffect(() => {
    if (match.status === 'in_progress' && match.match_start_time) {
      timerRef.current = setInterval(() => {
        setElapsed(formatElapsed(match.match_start_time));
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [match.status, match.match_start_time]);

  const handleStartMatch = async () => {
    setStartingMatch(true);
    try {
      await tournamentApi.updateMatch(match.id, { status: 'in_progress' });
      toast.success('Match started — timer running');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start match');
    } finally {
      setStartingMatch(false);
    }
  };

  const [matchScores, setMatchScores] = useState(() => {
    let existingScores = { game1_p1: '', game1_p2: '', game2_p1: '', game2_p2: '', game3_p1: '', game3_p2: '' };
    if (match.score) {
      try {
        // mysql2 returns JSON columns as objects; guard against both cases
        const parsed = typeof match.score === 'string' ? JSON.parse(match.score) : match.score;
        existingScores = {
          game1_p1: parsed.game1?.player1 ?? '',
          game1_p2: parsed.game1?.player2 ?? '',
          game2_p1: parsed.game2?.player1 ?? '',
          game2_p2: parsed.game2?.player2 ?? '',
          game3_p1: parsed.game3?.player1 ?? '',
          game3_p2: parsed.game3?.player2 ?? ''
        };
      } catch {}
    }
    return existingScores;
  });
  const [scoreErrors, setScoreErrors] = useState({ game1: '', game2: '', game3: '' });
  const [pointsPerSet, setPointsPerSet] = useState(match.points_per_set || 21);
  const [isWalkover, setIsWalkover] = useState(false);
  const [walkoverWinnerId, setWalkoverWinnerId] = useState('');

  // Derive sport-specific display mode
  const isTennis = sport === 'tennis' || sport === 'padel';
  const isTableTennis = sport === 'table_tennis' || sport === 'squash';
  const isPickleball = sport === 'pickleball';
  const gameLabel = isTennis ? 'SET' : 'GAME';
  const showBadmintonHint = !isTennis && !isTableTennis && !isPickleball;
  const hintPts = parseInt(pointsPerSet) || 21;
  const hintDeuce = hintPts - 1;
  const hintCap = hintPts === 15 ? 21 : 30;
  const hintSets = match.no_of_sets === 'best_of_1' ? 'Best of 1' : 'Best of 3';

  const getValidatorForGame = (p1Val, p2Val) => {
    const a = parseInt(p1Val) || 0;
    const b = parseInt(p2Val) || 0;
    if (isTennis) return validateTennisSet(a, b);
    if (isTableTennis) return validateTTGame(a, b, pointsPerSet || 11);
    if (isPickleball) return validatePickleballGame(a, b, pointsPerSet || 11);
    return validateBadmintonGame(p1Val, p2Val, pointsPerSet);
  };

  const handleScoreChange = (field, value) => {
    setMatchScores(prev => ({ ...prev, [field]: value }));
    const gameNum = field.includes('game1') ? 1 : field.includes('game2') ? 2 : 3;
    let p1, p2;
    const prev = { ...matchScores, [field]: value };
    if (gameNum === 1) { p1 = prev.game1_p1; p2 = prev.game1_p2; }
    else if (gameNum === 2) { p1 = prev.game2_p1; p2 = prev.game2_p2; }
    else { p1 = prev.game3_p1; p2 = prev.game3_p2; }
    const error = getValidatorForGame(p1, p2);
    setScoreErrors(prev => ({ ...prev, [`game${gameNum}`]: error }));
  };

  const handleSubmit = async () => {
    if (isWalkover) {
      if (!walkoverWinnerId) { toast.error('Please select a winner for the walkover'); return; }
      try {
        await tournamentApi.updateMatch(match.id, {
          winner_id: walkoverWinnerId,
          score: { games: 'Walkover', isWalkover: true },
          points_per_set: isTennis ? null : pointsPerSet,
          status: 'completed',
          notes: 'Walkover'
        });
        toast.success('Walkover recorded!');
        onSaved();
      } catch (err) { toast.error(err.response?.data?.error || 'Failed to save score'); }
      return;
    }

    const { game1_p1, game1_p2, game2_p1, game2_p2, game3_p1, game3_p2 } = matchScores;
    const g1p1 = parseInt(game1_p1) || 0, g1p2 = parseInt(game1_p2) || 0;
    const g2p1 = parseInt(game2_p1) || 0, g2p2 = parseInt(game2_p2) || 0;
    const g3p1 = parseInt(game3_p1) || 0, g3p2 = parseInt(game3_p2) || 0;

    for (const s of [{ game: 1, p1: g1p1, p2: g1p2 }, { game: 2, p1: g2p1, p2: g2p2 }, { game: 3, p1: g3p1, p2: g3p2 }]) {
      if (s.p1 > 0 || s.p2 > 0) {
        const err = getValidatorForGame(s.p1, s.p2);
        if (err) { toast.error(`Invalid score in ${gameLabel} ${s.game}: ${err}`); return; }
      }
    }

    let gamesWonP1 = 0, gamesWonP2 = 0;
    const playedGames = [];
    if (g1p1 > 0 || g1p2 > 0) { playedGames.push({ player1: g1p1, player2: g1p2 }); if (g1p1 > g1p2) gamesWonP1++; else if (g1p2 > g1p1) gamesWonP2++; }
    if (g2p1 > 0 || g2p2 > 0) { playedGames.push({ player1: g2p1, player2: g2p2 }); if (g2p1 > g2p2) gamesWonP1++; else if (g2p2 > g2p1) gamesWonP2++; }
    if (gamesWonP1 < 2 && gamesWonP2 < 2 && (g3p1 > 0 || g3p2 > 0)) { playedGames.push({ player1: g3p1, player2: g3p2 }); if (g3p1 > g3p2) gamesWonP1++; else if (g3p2 > g3p1) gamesWonP2++; }

    const winnerId = gamesWonP1 > gamesWonP2 ? match.player1_id : match.player2_id;
    const scoreData = {
      games: `${gamesWonP1}-${gamesWonP2}`,
      game1: playedGames[0] || { player1: 0, player2: 0 },
      game2: playedGames[1] || { player1: 0, player2: 0 },
      game3: playedGames[2] || { player1: 0, player2: 0 }
    };

    try {
      await tournamentApi.updateMatch(match.id, {
        winner_id: winnerId,
        score: scoreData,
        points_per_set: isTennis ? null : pointsPerSet,
        status: 'completed'
      });
      toast.success('Score saved!');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save score'); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px', padding: '2rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>Enter Match Score</h3>

        <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f8f9fa', borderRadius: '6px', fontSize: '0.9rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
            <span style={{ fontWeight: '600', color: 'var(--primary-color)' }}>{getRoundName(match.round_number, match.event_id)}</span>
            <span style={{ color: '#666', fontSize: '0.8rem' }}>M{match.match_number}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: '500' }}>{match.player1_name}</span>
            <span style={{ color: '#999', fontSize: '0.8rem' }}>vs</span>
            <span style={{ fontWeight: '500' }}>{match.player2_name}</span>
          </div>
          {/* Match time status row */}
          <div style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {match.status === 'in_progress' && match.match_start_time && elapsed ? (
              <span style={{ fontSize: '0.78rem', color: '#d97706', fontWeight: 600 }}>
                ⏱ In progress · {elapsed}
              </span>
            ) : match.status === 'completed' && match.match_start_time && match.match_end_time ? (
              <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                ⏱ Duration: {formatDuration(match.match_start_time, match.match_end_time)}
              </span>
            ) : match.status !== 'in_progress' && match.status !== 'completed' ? (
              <button
                className="btn btn-outline"
                style={{ fontSize: '0.78rem', padding: '3px 10px', height: 'auto' }}
                onClick={handleStartMatch}
                disabled={startingMatch}
              >
                {startingMatch ? 'Starting…' : '▶ Start Match'}
              </button>
            ) : <span />}
          </div>
        </div>

        <div style={{ marginBottom: '1rem', padding: '0.5rem', background: '#fff3cd', borderRadius: '4px', border: '1px solid #ffeeba' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: '500', fontSize: '0.9rem', color: '#856404' }}>
            <input type="checkbox" checked={isWalkover} onChange={(e) => setIsWalkover(e.target.checked)} />
            Mark as Walkover
          </label>
        </div>

        {isWalkover ? (
          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>Select Winner:</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {[{ id: match.player1_id, name: match.player1_name }, { id: match.player2_id, name: match.player2_name }].map(p => (
                <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.75rem', border: walkoverWinnerId === p.id ? '1px solid var(--primary-color)' : '1px solid #ddd', borderRadius: '4px', background: walkoverWinnerId === p.id ? '#f0f9ff' : 'white' }}>
                  <input type="radio" name="walkoverWinner" value={p.id} checked={walkoverWinnerId === p.id} onChange={(e) => setWalkoverWinnerId(e.target.value)} />
                  <span style={{ fontWeight: '500' }}>{p.name}</span>
                </label>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Scoring rules hint */}
            {showBadmintonHint && (
              <div style={{ marginBottom: '1rem', padding: '0.5rem 0.75rem', background: '#F0FDFA', border: '1px solid #99F6E4', borderRadius: '6px', fontSize: '0.8rem', color: '#115E59', lineHeight: '1.7' }}>
                <span style={{ fontWeight: '600' }}>{hintPts} pts · {hintSets}</span>
                {' | '}Win by 2
                {' · '}
                <span>Deuce at {hintDeuce}–{hintDeuce} → play until 2-pt lead</span>
                {' · '}
                <span>Golden point: <strong>{hintCap}–{hintCap - 1}</strong> (if {hintCap - 1}–{hintCap - 1} reached)</span>
              </div>
            )}

            {/* Scoring format: auto-selected from schedule settings (match.points_per_set). Hidden for now — uncomment to re-enable manual override. */}
            {false && !isTennis && (
              <div style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                <span style={{ fontWeight: '600' }}>Scoring Format:</span>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  {(isTableTennis || isPickleball ? [11] : [15, 21]).map(pts => (
                    <label key={pts} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer' }}>
                      <input type="radio" name="pointsPerSet" value={pts} checked={pointsPerSet === pts} onChange={() => setPointsPerSet(pts)} />
                      {pts} pts
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 4px' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '0 4px', fontSize: '0.8rem', color: '#666', fontWeight: '600' }}>PLAYER</th>
                    {[1, 2, 3].map(n => (
                      <th key={n} style={{ textAlign: 'center', width: '60px', fontSize: '0.8rem', color: '#666', fontWeight: '600' }}>{gameLabel} {n}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: match.player1_name, fields: ['game1_p1', 'game2_p1', 'game3_p1'] },
                    { label: match.player2_name, fields: ['game1_p2', 'game2_p2', 'game3_p2'] }
                  ].map((row, ri) => (
                    <tr key={ri}>
                      <td style={{ padding: '6px 4px', fontWeight: '500', fontSize: '0.95rem' }}>{row.label}</td>
                      {row.fields.map((field, fi) => {
                        const gameKey = `game${fi + 1}`;
                        const gpPairs = [['game1_p1','game1_p2'],['game2_p1','game2_p2'],['game3_p1','game3_p2']];
                        const [p1k, p2k] = gpPairs[fi];
                        const gp = showBadmintonHint && (() => {
                          const a = parseInt(matchScores[p1k]) || 0, b = parseInt(matchScores[p2k]) || 0;
                          return Math.max(a, b) === hintCap && Math.abs(a - b) === 1;
                        })();
                        return (
                          <td key={field} style={{ padding: '2px' }}>
                            <input
                              type="number"
                              className="form-input"
                              style={{ padding: '4px', textAlign: 'center', height: '36px', borderColor: scoreErrors[gameKey] ? '#dc3545' : gp ? '#f59e0b' : '' }}
                              value={matchScores[field]}
                              onChange={(e) => handleScoreChange(field, e.target.value)}
                              min="0"
                              max={isTennis ? '7' : '30'}
                              placeholder={isTennis ? '6' : '-'}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Golden point indicator row */}
                  {showBadmintonHint && (
                    <tr>
                      <td />
                      {[['game1_p1','game1_p2'],['game2_p1','game2_p2'],['game3_p1','game3_p2']].map(([p1k, p2k], i) => {
                        const a = parseInt(matchScores[p1k]) || 0, b = parseInt(matchScores[p2k]) || 0;
                        const gp = Math.max(a, b) === hintCap && Math.abs(a - b) === 1;
                        return (
                          <td key={i} style={{ textAlign: 'center', paddingTop: '3px' }}>
                            {gp && (
                              <span title="Golden Point" style={{ fontSize: '0.6rem', background: '#f59e0b', color: '#fff', borderRadius: 999, padding: '1px 6px', fontWeight: 700, letterSpacing: '0.02em' }}>
                                ⚡ GP
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                </tbody>
              </table>
              {(scoreErrors.game1 || scoreErrors.game2 || scoreErrors.game3) && (
                <div style={{ marginTop: '0.75rem', padding: '0.5rem', background: '#fff5f5', borderRadius: '4px', border: '1px solid #f5c6cb' }}>
                  {scoreErrors.game1 && <div style={{ fontSize: '0.8rem', color: '#dc3545' }}>{gameLabel} 1: {scoreErrors.game1}</div>}
                  {scoreErrors.game2 && <div style={{ fontSize: '0.8rem', color: '#dc3545' }}>{gameLabel} 2: {scoreErrors.game2}</div>}
                  {scoreErrors.game3 && <div style={{ fontSize: '0.8rem', color: '#dc3545' }}>{gameLabel} 3: {scoreErrors.game3}</div>}
                </div>
              )}
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit}>Save Score</button>
        </div>
      </div>
    </div>
  );
};

export default ScoreModal;
